
import { useCallback, useState, useEffect } from "react";
import { toast } from "sonner";
import { supabase } from "@/lib/supabase";
import type { QuizState } from "@/types/quiz";

export function useQuizScore(
  state: QuizState,
  setState: (state: QuizState | ((prev: QuizState) => QuizState)) => void,
) {
  const [recentAnswers, setRecentAnswers] = useState<boolean[]>([]);
  const [adaptiveAdjustmentCount, setAdaptiveAdjustmentCount] = useState(0);
  const [lastAnswerTime, setLastAnswerTime] = useState<number | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  
  useEffect(() => {
    setRecentAnswers([]);
    setAdaptiveAdjustmentCount(0);
    setLastAnswerTime(null);
  }, [state.selectedSubject, state.selectedCategory]);

  const validateDifficulty = (diff: any): "easy" | "medium" | "hard" => {
    if (diff === "easy" || diff === "medium" || diff === "hard") {
      return diff;
    }
    return "easy";
  };

  const analyzePerformanceAndAdjustDifficulty = useCallback(() => {
    if (recentAnswers.length < 3) return;

    const recentCorrect = recentAnswers.filter(Boolean).length;
    const accuracy = recentCorrect / recentAnswers.length;
    const currentDifficulty = validateDifficulty(state.difficulty);

    console.log('Analyzing real-time performance:', {
      recentAnswers,
      accuracy: `${Math.round(accuracy * 100)}%`,
      currentDifficulty
    });

    if (recentAnswers.length >= 3) {
      let newDifficulty = currentDifficulty;

      if (accuracy > 0.8) {
        newDifficulty = currentDifficulty === "easy" ? "medium" :
                      currentDifficulty === "medium" ? "hard" : "hard";
      } else if (accuracy < 0.4) {
        newDifficulty = currentDifficulty === "hard" ? "medium" :
                      currentDifficulty === "medium" ? "easy" : "easy";
      }

      if (newDifficulty !== currentDifficulty) {
        console.log(`Adjusting difficulty from ${currentDifficulty} to ${newDifficulty}`);
        setState(prev => ({
          ...prev,
          difficulty: newDifficulty
        }));
        
        if (adaptiveAdjustmentCount > 0) {
          toast.success(`Difficulty adjusted to ${newDifficulty} based on your recent performance`);
        }
        
        setAdaptiveAdjustmentCount(prev => prev + 1);
        
        setRecentAnswers([]);
      }
    }
  }, [recentAnswers, setState, state.difficulty, adaptiveAdjustmentCount]);

  useEffect(() => {
    if (recentAnswers.length >= 3) {
      analyzePerformanceAndAdjustDifficulty();
    }
  }, [recentAnswers, analyzePerformanceAndAdjustDifficulty]);

  const saveScore = useCallback(async (finalScore: number) => {
    try {
      setIsSaving(true);
      console.log('=== CRITICAL DEBUG: Starting saveScore with RPC function ===');
      console.log('Score to save:', finalScore);
      
      // Get current authenticated user
      const { data: authData } = await supabase.auth.getSession();
      
      if (!authData?.session?.user) {
        console.error('CRITICAL ERROR: No authenticated user found');
        toast.error("Please log in to save your progress");
        setIsSaving(false);
        return false;
      }

      const userId = authData.session.user.id;
      console.log('Authenticated user ID:', userId);

      // Ensure we have subject and category, using fallbacks if needed
      const subjectToSave = state.selectedSubject || state.subject || "General";
      const categoryToSave = state.selectedCategory || state.category || "General";
      
      if (!subjectToSave || !categoryToSave) {
        console.error('Missing subject or category:', { 
          subject: subjectToSave, 
          category: categoryToSave 
        });
        toast.error("Missing subject or category information");
        setIsSaving(false);
        return false;
      }

      console.log('Using subject/category:', {
        subject: subjectToSave,
        category: categoryToSave
      });

      const validDifficulty = validateDifficulty(state.difficulty);
      console.log('Validated difficulty:', validDifficulty);

      const totalQuestions = state.questionsCount || 20;
      const correctAnswers = Math.round((finalScore / 100) * totalQuestions);
      
      console.log('Performance Data to save:', {
        score: Math.round(finalScore),
        subject: subjectToSave,
        category: categoryToSave,
        difficulty: validDifficulty,
        user_id: userId,
        total_questions: totalQuestions,
        correct_answers: correctAnswers
      });

      // Use the RPC function to insert performance history
      const { error: insertError } = await supabase.rpc(
        'insert_performance_history',
        {
          p_score: Math.round(finalScore),
          p_subject: subjectToSave,
          p_category: categoryToSave,
          p_difficulty: validDifficulty,
          p_user_id: userId,
          p_total_questions: totalQuestions,
          p_correct_answers: correctAnswers
        }
      );

      if (insertError) {
        console.error('DATABASE INSERT ERROR:', insertError);
        console.error('Error code:', insertError.code);
        console.error('Error details:', insertError.details);
        console.error('Error hint:', insertError.hint);
        
        // Fallback to direct insert if RPC fails
        console.log('Attempting fallback direct insert...');
        const { error: directError } = await supabase
          .from('performance_history')
          .insert({
            score: Math.round(finalScore),
            subject: subjectToSave,
            category: categoryToSave,
            difficulty: validDifficulty,
            user_id: userId,
            total_questions: totalQuestions,
            correct_answers: correctAnswers,
            created_at: new Date(),
            date: new Date()
          });
          
        if (directError) {
          console.error('FALLBACK INSERT ERROR:', directError);
          toast.error("Failed to save your progress");
          setIsSaving(false);
          return false;
        } else {
          console.log('FALLBACK INSERT SUCCESS!');
        }
      } else {
        console.log('RPC INSERT SUCCESS!');
      }

      // Also update performance predictions table
      try {
        // Get recent performances for prediction calculation
        const { data: recentPerformances } = await supabase
          .from('performance_history')
          .select('score')
          .eq('user_id', userId)
          .eq('subject', subjectToSave)
          .order('created_at', { ascending: false })
          .limit(5);
          
        const recentScores = recentPerformances?.map(p => p.score) || [];
        const averageScore = recentScores.length > 0 
          ? recentScores.reduce((sum, score) => sum + score, 0) / recentScores.length 
          : finalScore;
          
        console.log('Updating performance predictions with recent scores:', recentScores);
          
        // Insert performance prediction
        const { error: predictionError } = await supabase
          .from('performance_predictions')
          .insert({
            user_id: userId,
            subject: subjectToSave,
            predicted_score: averageScore,
            confidence_level: 0.8,
            recommendations: [
              'Continue practicing with mixed difficulty questions',
              'Review incorrect answers for patterns',
              'Try focused practice on weak areas'
            ],
            created_at: new Date()
          });
            
        if (predictionError) {
          console.error('Failed to save performance prediction:', predictionError);
        } else {
          console.log('Performance prediction saved successfully');
        }
      } catch (predictionError) {
        console.error('Error in prediction calculation:', predictionError);
      }

      // Calculate new difficulty based on overall score
      let newDifficulty = validDifficulty;

      if (finalScore >= 80) {
        newDifficulty = validDifficulty === "easy" ? "medium" :
                      validDifficulty === "medium" ? "hard" : "hard";
      } else if (finalScore <= 40) {
        newDifficulty = validDifficulty === "hard" ? "medium" :
                      validDifficulty === "medium" ? "easy" : "easy";
      }

      console.log('Calculated new difficulty:', newDifficulty);

      if (newDifficulty !== validDifficulty) {
        setState(prev => ({
          ...prev,
          difficulty: newDifficulty
        }));
        console.log('Updated state with new difficulty:', newDifficulty);
        toast.success(`Difficulty level adjusted to ${newDifficulty}`);
      }

      toast.success("Performance saved successfully!");
      setIsSaving(false);
      return true;
    } catch (error) {
      console.error('CRITICAL ERROR in saveScore:', error);
      console.error('Error type:', typeof error);
      console.error('Error string:', String(error));
      if (error instanceof Error) {
        console.error('Error name:', error.name);
        console.error('Error message:', error.message);
        console.error('Error stack:', error.stack);
      }
      toast.error("Failed to save progress. Please try again.");
      setIsSaving(false);
      return false;
    }
  }, [state, setState]);

  const handleCorrectAnswer = useCallback(() => {
    setState(prev => ({
      ...prev,
      score: prev.score + 1,
      streak: prev.streak + 1
    }));

    setRecentAnswers(prev => [...prev, true]);
    
    const now = Date.now();
    if (lastAnswerTime) {
      const answerTime = now - lastAnswerTime;
      console.log('Answer time (ms):', answerTime);
    }
    setLastAnswerTime(now);

    const messages = {
      en: "Correct!",
      hi: "सही!",
      es: "¡Correcto!",
      fr: "Correct!",
      de: "Richtig!",
      zh: "正确！",
      ja: "正解！"
    };
    
    toast.success(messages[state.language || 'en']);
  }, [setState, state.language, lastAnswerTime]);

  const handleIncorrectAnswer = useCallback(() => {
    setState(prev => ({ 
      ...prev, 
      streak: 0
    }));
    
    setRecentAnswers(prev => [...prev, false]);
    
    const now = Date.now();
    if (lastAnswerTime) {
      const answerTime = now - lastAnswerTime;
      console.log('Answer time (ms):', answerTime);
    }
    setLastAnswerTime(now);
    
    const messages = {
      en: "Try again!",
      hi: "पुनः प्रयास करें!",
      es: "¡Inténtalo de nuevo!",
      fr: "Essaie encore!",
      de: "Versuch es nochmal!",
      zh: "再试一次！",
      ja: "もう一度！"
    };
    
    toast.error(messages[state.language || 'en']);
  }, [setState, state.language, lastAnswerTime]);

  return {
    saveScore,
    handleCorrectAnswer,
    handleIncorrectAnswer,
    isSaving
  };
}
